import {
  ActionIcon,
  Button,
  Card,
  Group,
  Modal,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { ClipboardList, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import type { DoctorSpecialty } from "@/lib/api/examinations";
import {
  listLabTemplates,
  type LabTemplate,
} from "@/lib/api/laboratory";

type ExtraFields = Record<string, unknown> | null | undefined;

interface Props {
  specialty: DoctorSpecialty;
  values: ExtraFields;
  onChange: (next: Record<string, unknown>) => void;
  disabled?: boolean;
  patientSex?: string | null;
}

function pick<T>(obj: ExtraFields, key: string, fallback: T): T {
  if (!obj) return fallback;
  const v = (obj as Record<string, unknown>)[key];
  return (v as T) ?? fallback;
}

export function SpecialtyExtraFields({
  specialty,
  values,
  onChange,
  disabled,
  patientSex,
}: Props) {
  const { t } = useTranslation();

  const update = (patch: Record<string, unknown>) => {
    onChange({ ...(values ?? {}), ...patch });
  };

  let body: React.ReactNode = null;

  switch (specialty) {
    case "ophthalmologist":
      body = (
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <TextInput
            label={t("section_extras.ophth.visual_acuity_right")}
            placeholder="1.0"
            disabled={disabled}
            value={pick(values, "visual_acuity_right", "")}
            onChange={(e) => update({ visual_acuity_right: e.currentTarget.value })}
          />
          <TextInput
            label={t("section_extras.ophth.visual_acuity_left")}
            placeholder="1.0"
            disabled={disabled}
            value={pick(values, "visual_acuity_left", "")}
            onChange={(e) => update({ visual_acuity_left: e.currentTarget.value })}
          />
          <NumberInput
            label={t("section_extras.ophth.iop_right")}
            placeholder="16"
            min={0}
            max={60}
            decimalScale={1}
            disabled={disabled}
            value={pick<number | "">(values, "iop_right", "")}
            onChange={(v) => update({ iop_right: v })}
          />
          <NumberInput
            label={t("section_extras.ophth.iop_left")}
            placeholder="16"
            min={0}
            max={60}
            decimalScale={1}
            disabled={disabled}
            value={pick<number | "">(values, "iop_left", "")}
            onChange={(v) => update({ iop_left: v })}
          />
          <Switch
            label={t("section_extras.ophth.color_vision_normal")}
            disabled={disabled}
            checked={pick(values, "color_vision_normal", true)}
            onChange={(e) => update({ color_vision_normal: e.currentTarget.checked })}
          />
        </SimpleGrid>
      );
      break;

    case "ent":
      body = (
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <NumberInput
            label={t("section_extras.ent.hearing_left_db")}
            placeholder="20"
            min={-10}
            max={120}
            disabled={disabled}
            value={pick<number | "">(values, "hearing_left_db", "")}
            onChange={(v) => update({ hearing_left_db: v })}
          />
          <NumberInput
            label={t("section_extras.ent.hearing_right_db")}
            placeholder="20"
            min={-10}
            max={120}
            disabled={disabled}
            value={pick<number | "">(values, "hearing_right_db", "")}
            onChange={(v) => update({ hearing_right_db: v })}
          />
          <Select
            label={t("section_extras.ent.throat")}
            placeholder={t("placeholder.select")}
            disabled={disabled}
            data={[
              { value: "normal", label: t("section_extras.ent.normal") },
              { value: "hyperemic", label: t("section_extras.ent.hyperemic") },
              { value: "tonsillitis", label: t("section_extras.ent.tonsillitis") },
              { value: "other", label: t("section_extras.ent.other") },
            ]}
            value={pick(values, "throat", "")}
            onChange={(v) => update({ throat: v ?? "" })}
          />
          <Select
            label={t("section_extras.ent.nose")}
            placeholder={t("placeholder.select")}
            disabled={disabled}
            data={[
              { value: "normal", label: t("section_extras.ent.normal") },
              { value: "deviated_septum", label: t("section_extras.ent.deviated_septum") },
              { value: "rhinitis", label: t("section_extras.ent.rhinitis") },
              { value: "other", label: t("section_extras.ent.other") },
            ]}
            value={pick(values, "nose", "")}
            onChange={(v) => update({ nose: v ?? "" })}
          />
        </SimpleGrid>
      );
      break;

    case "cardiologist":
      body = (
        <Stack gap="md">
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <Select
              label={t("section_extras.card.rhythm")}
              placeholder={t("placeholder.select")}
              disabled={disabled}
              data={[
                { value: "sinus", label: t("section_extras.card.sinus") },
                { value: "atrial_fibrillation", label: t("section_extras.card.atrial_fibrillation") },
                { value: "other", label: t("section_extras.card.other") },
              ]}
              value={pick(values, "rhythm", "")}
              onChange={(v) => update({ rhythm: v ?? "" })}
            />
            <NumberInput
              label={t("section_extras.card.heart_rate")}
              placeholder="72"
              min={20}
              max={250}
              disabled={disabled}
              value={pick<number | "">(values, "heart_rate", "")}
              onChange={(v) => update({ heart_rate: v })}
            />
            <Select
              label={t("section_extras.card.murmurs")}
              placeholder={t("placeholder.select")}
              disabled={disabled}
              data={[
                { value: "none", label: t("section_extras.card.none") },
                { value: "systolic", label: t("section_extras.card.systolic") },
                { value: "diastolic", label: t("section_extras.card.diastolic") },
              ]}
              value={pick(values, "murmurs", "")}
              onChange={(v) => update({ murmurs: v ?? "" })}
            />
            <Switch
              label={t("section_extras.card.ecg_attached")}
              disabled={disabled}
              checked={pick(values, "ecg_attached", false)}
              onChange={(e) => update({ ecg_attached: e.currentTarget.checked })}
            />
          </SimpleGrid>
          <Textarea
            label={t("section_extras.card.echocardiogram_summary")}
            placeholder=""
            minRows={2}
            autosize
            disabled={disabled}
            value={pick(values, "echocardiogram_summary", "")}
            onChange={(e) => update({ echocardiogram_summary: e.currentTarget.value })}
          />
        </Stack>
      );
      break;

    case "gynecologist":
      if (patientSex && patientSex !== "female") {
        body = (
          <Text c="dimmed" size="sm">
            {t("section_extras.gyn.female_only")}
          </Text>
        );
        break;
      }
      body = (
        <Stack gap="md">
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <TextInput
              label={t("section_extras.gyn.last_period_date")}
              type="date"
              disabled={disabled}
              value={pick(values, "last_period_date", "")}
              onChange={(e) => update({ last_period_date: e.currentTarget.value })}
            />
            <Switch
              label={t("section_extras.gyn.pregnancy")}
              disabled={disabled}
              checked={pick(values, "pregnancy", false)}
              onChange={(e) => update({ pregnancy: e.currentTarget.checked })}
            />
          </SimpleGrid>
          <Textarea
            label={t("section_extras.gyn.obstetric_history")}
            minRows={2}
            autosize
            disabled={disabled}
            value={pick(values, "obstetric_history", "")}
            onChange={(e) => update({ obstetric_history: e.currentTarget.value })}
          />
          <Textarea
            label={t("section_extras.gyn.external_exam")}
            minRows={2}
            autosize
            disabled={disabled}
            value={pick(values, "external_exam", "")}
            onChange={(e) => update({ external_exam: e.currentTarget.value })}
          />
          <Textarea
            label={t("section_extras.gyn.internal_exam")}
            minRows={2}
            autosize
            disabled={disabled}
            value={pick(values, "internal_exam", "")}
            onChange={(e) => update({ internal_exam: e.currentTarget.value })}
          />
        </Stack>
      );
      break;

    case "psychiatrist_narcologist":
      body = (
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <Select
            label={t("section_extras.psy.mental_status")}
            placeholder={t("placeholder.select")}
            disabled={disabled}
            data={[
              { value: "clear", label: t("section_extras.psy.clear") },
              { value: "anxious", label: t("section_extras.psy.anxious") },
              { value: "depressed", label: t("section_extras.psy.depressed") },
              { value: "altered", label: t("section_extras.psy.altered") },
            ]}
            value={pick(values, "mental_status", "")}
            onChange={(v) => update({ mental_status: v ?? "" })}
          />
          <Select
            label={t("section_extras.psy.substance_use")}
            placeholder={t("placeholder.select")}
            disabled={disabled}
            data={[
              { value: "none", label: t("section_extras.psy.no_use") },
              { value: "occasional", label: t("section_extras.psy.occasional") },
              { value: "regular", label: t("section_extras.psy.regular") },
            ]}
            value={pick(values, "substance_use", "")}
            onChange={(v) => update({ substance_use: v ?? "" })}
          />
          <Switch
            label={t("section_extras.psy.driving_clearance")}
            disabled={disabled}
            checked={pick(values, "driving_clearance", true)}
            onChange={(e) => update({ driving_clearance: e.currentTarget.checked })}
          />
        </SimpleGrid>
      );
      break;

    case "dentist":
      body = (
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <NumberInput
            label={t("section_extras.dent.caries_count")}
            placeholder="0"
            min={0}
            max={32}
            disabled={disabled}
            value={pick<number | "">(values, "caries_count", "")}
            onChange={(v) => update({ caries_count: v })}
          />
          <NumberInput
            label={t("section_extras.dent.missing_teeth")}
            placeholder="0"
            min={0}
            max={32}
            disabled={disabled}
            value={pick<number | "">(values, "missing_teeth", "")}
            onChange={(v) => update({ missing_teeth: v })}
          />
          <Select
            label={t("section_extras.dent.gum_status")}
            placeholder={t("placeholder.select")}
            disabled={disabled}
            data={[
              { value: "healthy", label: t("section_extras.dent.healthy") },
              { value: "gingivitis", label: t("section_extras.dent.gingivitis") },
              { value: "periodontitis", label: t("section_extras.dent.periodontitis") },
            ]}
            value={pick(values, "gum_status", "")}
            onChange={(v) => update({ gum_status: v ?? "" })}
          />
        </SimpleGrid>
      );
      break;

    case "dermatovenerologist":
      body = (
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <Switch
            label={t("section_extras.derm.skin_lesions")}
            disabled={disabled}
            checked={pick(values, "skin_lesions", false)}
            onChange={(e) => update({ skin_lesions: e.currentTarget.checked })}
          />
          <Switch
            label={t("section_extras.derm.std_signs")}
            disabled={disabled}
            checked={pick(values, "std_signs", false)}
            onChange={(e) => update({ std_signs: e.currentTarget.checked })}
          />
          <Switch
            label={t("section_extras.derm.food_handler_clear")}
            disabled={disabled}
            checked={pick(values, "food_handler_clear", true)}
            onChange={(e) => update({ food_handler_clear: e.currentTarget.checked })}
          />
        </SimpleGrid>
      );
      break;

    case "surgeon":
      body = (
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <Switch
            label={t("section_extras.surg.hernias")}
            disabled={disabled}
            checked={pick(values, "hernias", false)}
            onChange={(e) => update({ hernias: e.currentTarget.checked })}
          />
          <Switch
            label={t("section_extras.surg.varicose_veins")}
            disabled={disabled}
            checked={pick(values, "varicose_veins", false)}
            onChange={(e) => update({ varicose_veins: e.currentTarget.checked })}
          />
          <Switch
            label={t("section_extras.surg.lymphadenopathy")}
            disabled={disabled}
            checked={pick(values, "lymphadenopathy", false)}
            onChange={(e) => update({ lymphadenopathy: e.currentTarget.checked })}
          />
        </SimpleGrid>
      );
      break;

    case "laboratory":
      body = (
        <LabTemplateForm
          templateId={pick<string | null>(values, "lab_template_id", null)}
          results={pick<Record<string, string>>(values, "lab_results", {})}
          onChange={(patch) => update(patch)}
          disabled={disabled}
        />
      );
      break;

    case "neurologist":
      body = (
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <Select
            label={t("section_extras.neu.reflexes")}
            placeholder={t("placeholder.select")}
            disabled={disabled}
            data={[
              { value: "normal", label: t("section_extras.neu.normal") },
              { value: "hyperreflexia", label: t("section_extras.neu.hyperreflexia") },
              { value: "hyporeflexia", label: t("section_extras.neu.hyporeflexia") },
            ]}
            value={pick(values, "reflexes", "")}
            onChange={(v) => update({ reflexes: v ?? "" })}
          />
          <Select
            label={t("section_extras.neu.coordination")}
            placeholder={t("placeholder.select")}
            disabled={disabled}
            data={[
              { value: "normal", label: t("section_extras.neu.normal") },
              { value: "ataxia", label: t("section_extras.neu.ataxia") },
            ]}
            value={pick(values, "coordination", "")}
            onChange={(v) => update({ coordination: v ?? "" })}
          />
          <Switch
            label={t("section_extras.neu.headaches")}
            disabled={disabled}
            checked={pick(values, "headaches", false)}
            onChange={(e) => update({ headaches: e.currentTarget.checked })}
          />
        </SimpleGrid>
      );
      break;

    default:
      return null;
  }

  return (
    <Card padding="xl" radius="xl">
      <Group gap="sm" mb="lg" align="center">
        <ThemeIcon size={36} radius="xl" variant="light" color="brand">
          <ClipboardList size={18} strokeWidth={2} />
        </ThemeIcon>
        <Title order={5} fw={700}>
          {t(`section_extras.title.${specialty}`)}
        </Title>
      </Group>
      {body}
    </Card>
  );
}


// results: { "<row_index>": "value", ... } — only the result column
type LabResults = Record<string, string>;

function LabTemplateForm({
  templateId,
  results,
  onChange,
  disabled,
}: {
  templateId: string | null;
  results: LabResults;
  onChange: (patch: Record<string, unknown>) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();

  const { data: templates } = useQuery<LabTemplate[]>({
    queryKey: ["lab-templates"],
    queryFn: listLabTemplates,
    staleTime: 60_000,
  });

  // No auto-selection: each patient may need a different template (RW only,
  // CBC, urine, etc.), so the lab tech explicitly picks for every section.
  const tpl = (templates ?? []).find((x) => x.id === templateId) ?? null;

  const setResult = (rowIdx: number, value: string) => {
    onChange({ lab_results: { ...(results ?? {}), [String(rowIdx)]: value } });
  };

  const pickTemplate = (id: string | null) => {
    if (!id) {
      onChange({ lab_template_id: null, lab_results: {} });
      return;
    }
    const newTpl = (templates ?? []).find((x) => x.id === id);
    onChange({
      lab_template_id: id,
      lab_template_name: newTpl?.name ?? null,
      lab_results: {},
    });
  };

  if (!tpl) {
    return (
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          {t("section_extras.lab.pick_template_hint")}
        </Text>
        <Select
          label={t("section_extras.lab.template")}
          placeholder={t("section_extras.lab.pick_template")}
          searchable
          data={(templates ?? []).map((x) => ({ value: x.id, label: x.name }))}
          value={templateId}
          onChange={pickTemplate}
          disabled={disabled}
        />
      </Stack>
    );
  }

  const resultIdx = tpl.result_col_index;

  return (
    <Stack gap="md">
      <Group justify="space-between" wrap="wrap" align="flex-end">
        <Stack gap={2} style={{ flex: 1, minWidth: 240 }}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
            {t("section_extras.lab.template")}
          </Text>
          <Text fw={700}>{tpl.name}</Text>
          {tpl.description && (
            <Text size="xs" c="dimmed">{tpl.description}</Text>
          )}
        </Stack>
        {!disabled && (
          <Button
            size="xs"
            variant="subtle"
            color="gray"
            onClick={() => pickTemplate(null)}
          >
            {t("section_extras.lab.change_template")}
          </Button>
        )}
      </Group>

      <Table.ScrollContainer minWidth={640}>
        <Table verticalSpacing="xs" striped withTableBorder highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              {tpl.headers.map((h, i) => (
                <Table.Th
                  key={i}
                  style={
                    i === resultIdx
                      ? { background: "var(--mantine-color-yellow-0)" }
                      : undefined
                  }
                >
                  {h}
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {tpl.data_rows.map((row, ri) => (
              <Table.Tr key={ri}>
                {row.map((cell, ci) => (
                  <Table.Td
                    key={ci}
                    style={
                      ci === resultIdx
                        ? { background: "var(--mantine-color-yellow-0)" }
                        : undefined
                    }
                  >
                    {ci === resultIdx ? (
                      <TextInput
                        size="xs"
                        placeholder="—"
                        disabled={disabled}
                        value={results?.[String(ri)] ?? ""}
                        onChange={(e) => setResult(ri, e.currentTarget.value)}
                      />
                    ) : (
                      <Text size="xs" style={{ whiteSpace: "pre-line" }}>
                        {cell}
                      </Text>
                    )}
                  </Table.Td>
                ))}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      {resultIdx === null && (
        <Text size="xs" c="dimmed">
          {t("section_extras.lab.no_result_col_warn")}
        </Text>
      )}
    </Stack>
  );
}


